import React from 'react';
import { BrandProvider } from '../themes/BrandProvider';
import { Layout } from '../Layout/Layout';
import { GlobalConfig, GlobalProvider } from '../hooks/useGlobal';
import { PlacesAutocompleteProvider } from '../hooks/usePlacesAutoComplete';
import { AnalyticsClient, MapProvider } from 'rwl-ui';
import {
    ConfigFetcher,
    ContentListsProvider,
    ContentFetcher,
} from '../fetchers';
import { MountedQueryClientProvider } from 'rwl-shared/dist/hooks';
import {
    AnalyticsProvider,
    TriggerLoadEvent,
} from '../hooks/useAnalyticsProviders';
import { BreakpointProvider, ContainerWidthProvider } from '../providers';
import { MapProvider as NeighborhoodMapProvider } from '../hooks/MapProvider';
import { ParentPageIntersectionProvider } from '../hooks/intersectionObservers/useParentPageIntersection';
import { JobProvider } from '../fetchers/JobFetcher';
import { ReviewProvider } from '../fetchers/ReviewFetcher';
import { SlideOutTab } from '../components';

type WidgetProps = GlobalConfig & {
    pluginPropertyId?: string;
    analytics?: {
        client?: AnalyticsClient;
    };
};

export const Widget = (props: WidgetProps): JSX.Element => {
    const { pluginPropertyId, analytics, ...globalConfig } = props;

    const mainContent = (
        <>
            <TriggerLoadEvent />
            <MountedQueryClientProvider>
                <ContentListsProvider>
                    <ConfigFetcher />
                    <JobProvider>
                        <ReviewProvider>
                            <BrandProvider>
                                <ContainerWidthProvider>
                                    <BreakpointProvider>
                                        <MapProvider>
                                            <PlacesAutocompleteProvider>
                                                <NeighborhoodMapProvider>
                                                    {/* ContentFetcher must be inside PlacesAutocompleteProvider */}
                                                    <ContentFetcher />
                                                    <ParentPageIntersectionProvider>
                                                        <Layout />
                                                    </ParentPageIntersectionProvider>
                                                </NeighborhoodMapProvider>
                                            </PlacesAutocompleteProvider>
                                        </MapProvider>
                                    </BreakpointProvider>
                                </ContainerWidthProvider>
                            </BrandProvider>
                        </ReviewProvider>
                    </JobProvider>
                </ContentListsProvider>
            </MountedQueryClientProvider>
        </>
    );

    // If bumpOutTest is enabled and bumpOutTest object exists, wrap with SlideOutTab
    if (globalConfig.bumpOutTestEnabled && globalConfig.bumpOutTest) {
        return (
            <GlobalProvider {...globalConfig}>
                <AnalyticsProvider
                    pluginPropertyId={pluginPropertyId}
                    client={analytics?.client}
                >
                    <SlideOutTab>{mainContent}</SlideOutTab>
                </AnalyticsProvider>
            </GlobalProvider>
        );
    }

    // Return content without SlideOutTab if not enabled
    return (
        <GlobalProvider {...globalConfig}>
            <AnalyticsProvider
                pluginPropertyId={pluginPropertyId}
                client={analytics?.client}
            >
                {mainContent}
            </AnalyticsProvider>
        </GlobalProvider>
    );
};
